import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import {
  ColombiaService,
  RegistroVitaTx,
} from '../../../services/colombia.service';
import { PeruService } from '../../../services/peru.service';

/** Resultado del dialog: 'resuelta' → el caller refresca la tabla. */
export type VitaRegistroDetalleResult = 'resuelta' | undefined;

@Component({
  selector: 'app-vita-registro-detalle',
  templateUrl: './vita-registro-detalle.component.html',
  styleUrls: ['./vita-registro-detalle.component.sass'],
})
export class VitaRegistroDetalleComponent {
  t: RegistroVitaTx;

  // ─── Resolución manual (WS3.1) — solo si está en revisión ───
  /** Form de rechazo desplegado. */
  mostrarRechazo = false;
  motivo = '';
  /** Cuál acción está viajando (deshabilita todas). */
  resolviendo: 'ENVIADA' | 'RECHAZADA' | 'REANUDAR' | 'ANULAR' | null = null;
  resolverError: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<VitaRegistroDetalleComponent, VitaRegistroDetalleResult>,
    @Inject(MAT_DIALOG_DATA) public data: { tx: RegistroVitaTx },
    private svc: ColombiaService,
    private peruSvc: PeruService,
  ) {
    this.t = data.tx;
  }

  /** El rail de esta fila es Perú (resuelve contra el endpoint peru/*). */
  get esPeru(): boolean {
    return (this.t.rail || '').toUpperCase() === 'PE' || this.t.moneda === 'PEN';
  }

  get enRevision(): boolean {
    return this.t.status === 'REQUIRES_HUMAN_REVIEW';
  }

  // ─── Remesas con origen CLP (25/10/2026) ───
  // El débito CLP lo escribe wallet-back por la cola y puede no existir (envío
  // incierto). Revertir sin débito regalaría CLP; pagar sin débito, también.

  get origenClp(): boolean {
    return this.t.origen?.moneda === 'CLP';
  }

  /** «Rechazar y revertir» deshabilitado: remesa CLP en revisión SIN débito. */
  get rechazoBloqueado(): boolean {
    return this.enRevision && this.origenClp && this.t.debitoClp === false;
  }

  readonly motivoRechazoBloqueado =
    'No hubo débito CLP de esta remesa: no hay nada que revertir. Usa Anular.';

  /** Reanudar: remesa CLP en revisión CON débito (vuelve a la cola de pago). */
  get puedeReanudar(): boolean {
    return this.enRevision && this.origenClp && this.t.debitoClp === true;
  }

  /** Anular: remesa CLP en revisión SIN débito (no se paga ni se devuelve nada). */
  get puedeAnular(): boolean {
    return this.enRevision && this.origenClp && this.t.debitoClp === false;
  }

  /** Reanudar: el débito existe → la remesa vuelve a PENDING y el ejecutor la paga. */
  async reanudar(): Promise<void> {
    if (this.resolviendo || !this.puedeReanudar) return;
    const r = await Swal.fire({
      icon: 'question',
      title: 'Reanudar el pago',
      html: `<p style="font-size:0.9rem;color:#374151;margin:0;">El débito CLP de la orden <b class="font-mono">#${this.t.txId}</b> está registrado. La remesa vuelve a la cola y se paga en el próximo ciclo.</p>
        <p style="font-size:0.8rem;color:#6b7280;margin-top:8px;">¿Verificaste en Vita que la orden NO salió? Si ya salió, usá «Confirmar ENVIADA».</p>`,
      showCancelButton: true,
      confirmButtonText: 'Sí, reanudar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1C593D',
      reverseButtons: true,
    });
    if (!r.isConfirmed) return;
    await this.resolver('REANUDAR', this.motivo.trim() || undefined);
  }

  /** Anular: no hubo débito → la orden se cierra ANULADA, sin pago ni reversa. */
  async anular(): Promise<void> {
    if (this.resolviendo || !this.puedeAnular) return;
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Anular la remesa',
      html: `<p style="font-size:0.9rem;color:#374151;margin:0;">No hay débito CLP de la orden <b class="font-mono">#${this.t.txId}</b>. Se anula: no se paga y no se devuelve nada (el cliente no pagó).</p>
        <p style="font-size:0.8rem;color:#6b7280;margin-top:8px;">¿Verificaste en Vita que la orden NO existe?</p>`,
      showCancelButton: true,
      confirmButtonText: 'Sí, anular',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#6b7280',
      reverseButtons: true,
    });
    if (!r.isConfirmed) return;
    await this.resolver('ANULAR', this.motivo.trim() || undefined);
  }

  /** Estado a mostrar: un FAILED cuya transacción quedó ANULADA se muestra «Anulada». */
  estadoDe(t: RegistroVitaTx): string {
    return t.status === 'FAILED' && t.txEstado === 'ANULADA' ? 'ANULADA' : t.status;
  }

  /**
   * Confirmar ENVIADA: el operador verificó en el panel de Vita que el COP
   * SÍ salió. No hay reversa — solo cierra COMPLETED + tx ENVIADA.
   */
  async confirmarEnviada(): Promise<void> {
    if (this.resolviendo) return;
    const r = await Swal.fire({
      icon: 'question',
      title: 'Confirmar como ENVIADA',
      html: `<p style="font-size:0.9rem;color:#374151;margin:0;">¿Verificaste en el panel de <b>Vita</b> que la orden <b class="font-mono">#${this.t.txId}</b> figura <b>completada</b>?</p>
        <p style="font-size:0.8rem;color:#6b7280;margin-top:8px;">El cliente recibirá el correo de envío completado. No hay reversa.</p>`,
      showCancelButton: true,
      confirmButtonText: 'Sí, está completada en Vita',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1C593D',
      reverseButtons: true,
    });
    if (!r.isConfirmed) return;
    await this.resolver('ENVIADA');
  }

  /** Despliega el form de motivo (paso 1 del rechazo). */
  abrirRechazo(): void {
    if (this.resolviendo || this.rechazoBloqueado) return;
    this.mostrarRechazo = true;
    this.resolverError = null;
  }

  cancelarRechazo(): void {
    this.mostrarRechazo = false;
    this.motivo = '';
  }

  /**
   * Rechazar (paso 2): motivo obligatorio + confirmación explícita — dispara
   * la REVERSA del débito USDT. El guardrail evita el doble pago: si la orden
   * SÍ existe en Vita y además revertimos, el cliente cobra dos veces.
   */
  async rechazar(): Promise<void> {
    if (this.resolviendo) return;
    if (this.rechazoBloqueado) {
      this.resolverError = this.motivoRechazoBloqueado;
      return;
    }
    const motivo = this.motivo.trim();
    if (!motivo) {
      this.resolverError = 'El motivo es obligatorio para rechazar.';
      return;
    }
    this.resolverError = null;
    const r = await Swal.fire({
      icon: 'warning',
      title: '¿Rechazar y devolver el dinero?',
      html: `<p style="font-size:0.9rem;color:#374151;margin:0;">Esto devuelve <b>${
        this.t.origen?.moneda === 'CLP'
          ? Math.round(Number(this.t.montoOrigen)).toLocaleString('es-CL') + ' CLP'
          : Number(this.t.montoUsd).toFixed(2) + ' USDT'
      }</b> al cliente (reversa).</p>
        <p style="font-size:0.88rem;color:#b91c1c;font-weight:700;margin-top:10px;">¿Verificaste en Vita que la orden #${this.t.txId} NO existe?</p>
        <p style="font-size:0.78rem;color:#6b7280;margin-top:8px;">Si la orden sí salió y además revertís, el cliente cobra DOS veces.</p>`,
      showCancelButton: true,
      confirmButtonText: 'Sí, NO existe — rechazar y revertir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
      reverseButtons: true,
    });
    if (!r.isConfirmed) return;
    await this.resolver('RECHAZADA', motivo);
  }

  private async resolver(
    resultado: 'ENVIADA' | 'RECHAZADA' | 'REANUDAR' | 'ANULAR',
    motivo?: string,
  ): Promise<void> {
    this.resolviendo = resultado;
    this.resolverError = null;
    try {
      // Rutea por rail: PE → endpoint peru/*, resto → colombia/*.
      if (this.esPeru) {
        await this.peruSvc.resolverRemesa(this.t.txId, resultado, motivo);
      } else {
        await this.svc.resolverRemesa(this.t.txId, resultado, motivo);
      }
      this.dialogRef.close('resuelta');
    } catch (e: any) {
      // 409 del backend = ya no está en revisión (otro operador la cerró).
      this.resolverError =
        e?.error?.message || e?.message || 'No se pudo resolver la remesa.';
    } finally {
      this.resolviendo = null;
    }
  }

  estadoLabel(s: string): string {
    switch (s) {
      case 'PENDING':
      case 'PROCESSING':
        return 'En proceso';
      case 'SENT':
        return 'Enviada a Vita';
      case 'COMPLETED':
        return 'Completada';
      case 'FAILED':
        return 'Rechazada';
      case 'REQUIRES_HUMAN_REVIEW':
        return 'En revisión';
      case 'ANULADA':
        return 'Anulada';
      default:
        return s;
    }
  }

  getStatusBg(s: string): string {
    switch (s) {
      case 'COMPLETED':
        return '#15803d';
      case 'SENT':
        return '#16a34a';
      case 'FAILED':
        return '#dc2626';
      case 'PENDING':
      case 'PROCESSING':
        return '#f59e0b';
      case 'REQUIRES_HUMAN_REVIEW':
        return '#8b5cf6';
      case 'ANULADA':
        return '#6b7280';
      default:
        return '#f3f4f6';
    }
  }

  getStatusColor(s: string): string {
    return this.getStatusBg(s) === '#f3f4f6' ? '#6b7280' : '#ffffff';
  }

  // ── Moneda de destino (multi-rail: COP entero / PEN 2 dec.) ──
  recibeDe(t: RegistroVitaTx): number {
    return t.recibe != null ? t.recibe : t.recibeCop;
  }
  monedaDe(t: RegistroVitaTx): string {
    return (t.moneda || t.destino?.moneda || 'COP') as string;
  }
  fmtDe(t: RegistroVitaTx): string {
    return (t.decimales ?? 0) >= 2 ? '1.2-2' : '1.0-0';
  }

  close(): void {
    this.dialogRef.close();
  }
}
